// Pure helpers for the participant poll board (PollPage), kept out of the
// component so they can be unit-tested.

/** Beds a claim asks for when the input is left empty. */
export const DEFAULT_CLAIM_BEDS = 1;

/**
 * Parse the board's "beds for me" input. Empty or unparseable falls back to
 * DEFAULT_CLAIM_BEDS -- the input shows that as a placeholder rather than a
 * pre-filled value, so it can't be mistaken for anyone's existing count. An
 * explicit 0 stays 0 (claim the duty without reserving beds).
 */
export function parseBedsInput(raw: string | undefined): number {
  const n = Number.parseInt((raw ?? '').trim(), 10);
  if (!Number.isFinite(n)) return DEFAULT_CLAIM_BEDS;
  return Math.min(1000, Math.max(0, n));
}

/**
 * The new bed count to save when a participant edits their own claim, or null
 * when there is nothing to save. Unlike parseBedsInput, an empty or
 * unparseable input means "no change" rather than DEFAULT_CLAIM_BEDS, so
 * clearing the field never silently rewrites the claim.
 */
export function editedBeds(raw: string | undefined, current: number): number | null {
  if (!Number.isFinite(Number.parseInt((raw ?? '').trim(), 10))) return null;
  const n = parseBedsInput(raw);
  return n === current ? null : n;
}

/** Up to `max` claimant labels, then "+n" -- claimants are already filtered to
 *  active claims and ordered by rank server-side. Each label carries the
 *  claimant's own bed count via `label(name, beds)`. */
export function claimantLabels(
  claimants: { name: string; beds: number }[],
  label: (name: string, beds: number) => string,
  more: (n: number) => string,
  max = 3,
): string {
  const labels = claimants.map((c) => label(c.name, c.beds));
  if (labels.length <= max) return labels.join(', ');
  return `${labels.slice(0, max).join(', ')} ${more(labels.length - max)}`;
}
